/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    isValid,
    TextInputKit,
    CONTROL_SIZE,
    FieldLayout,
    PasswordInput,
    Textarea,
    TextInput,
} from '@veeam-vspc/components';
import { usePageWizardStore } from '@veeam-vspc/components/src/PageWizard/hooks/use-page-wizard-store';

import type {
    WizardStep,
    WizardStepData,
} from '@veeam-vspc/components';

import { PortalUserRoles } from 'core/entries/portal-user/enums';
import { useLang } from 'views/providers/LangProvider/hooks';
import { CommonService } from 'core/services/common-service';
import { stepValidate } from './helpers/stepValidate';
import { LoginInfoForm } from './components/LoginInfoForm';
import { SelectUserLink } from './components/SelectUserLink';
import { SelectUser } from './components/SelectUser';
import { VacInternalItemType } from 'core/enums/vac-internal-item-type';

import type { CompanyUserModel } from '../../../CompanyUser/interfaces';

export const LoginInfo: React.FC<WizardStepData<CompanyUserModel>> = (wizardStepData) => {
    const { validationState } = wizardStepData;
    const lang = useLang();
    const { data, isEdit } = wizardStepData;
    const isCompanyOwner = data.userRole === PortalUserRoles.CompanyOwner;
    const isSubtenant = data.userRole === PortalUserRoles.Subtenant;
    const wizardContext = usePageWizardStore<CompanyUserModel>();
    const openPanel = () => wizardContext.openPanel(hide => <SelectUser {...wizardStepData} hidePanel={hide} />);

    if (data.isVcdCompany && isCompanyOwner) {
        return (
            <LoginInfoForm {...wizardStepData}>
                <FieldLayout
                    label={`${lang.LOGIN}:`}
                    disabled={true}
                >
                    <TextInput
                        name='login'
                    />
                </FieldLayout>

                <FieldLayout
                    label={`${lang.NEW_PASSWORD}:`}
                    disabled={true}
                >
                    <PasswordInput
                        name='password'
                        autocomplete='new-password'
                    />
                </FieldLayout>

                <FieldLayout
                    label={`${lang.CONFIRM_PASSWORD}:`}
                    disabled={true}
                >
                    <PasswordInput
                        name='confirmPassword'
                        autocomplete='new-password'
                    />
                </FieldLayout>
            </LoginInfoForm>
        );
    }

    if (data.isVcdCompany && isSubtenant) {
        if (validationState.isForce()) {
            validationState.markAsHandledForce();

            if (!isEdit && !data.vcdUserId) {
                openPanel();
            }
        }

        return (
            <LoginInfoForm {...wizardStepData}>
                <FieldLayout
                    label={`${lang.LOGIN}:`}
                    disabled={true}
                    suffix={!isEdit && <SelectUserLink openPanel={openPanel} />}
                >
                    <TextInputKit
                        value={isEdit ? data.login : data.vcdUserName}
                        size={CONTROL_SIZE.l}
                    />
                </FieldLayout>

                <Textarea
                    name='description'
                    label={lang.DESCRIPTION}
                    rows={8}
                    size={CONTROL_SIZE.l}
                />
            </LoginInfoForm>
        );
    }

    return (
        <LoginInfoForm {...wizardStepData}>
            <FieldLayout label={`${lang.LOGIN}:`} disabled={isEdit}>
                <TextInput
                    name='login'
                />
            </FieldLayout>

            {data.selfEdit && (
                <FieldLayout label={`${lang.CURRENT_PASSWORD}:`}>
                    <PasswordInput
                        name='oldPassword'
                        autocomplete='new-password'
                    />
                </FieldLayout>
            )}

            <FieldLayout label={isEdit ? `${lang.NEW_PASSWORD}:` : `${lang.PASSWORD}:`}>
                <PasswordInput
                    name='password'
                    autocomplete='new-password'
                />
            </FieldLayout>

            <FieldLayout label={`${lang.CONFIRM_PASSWORD}:`}>
                <PasswordInput
                    name='confirmPassword'
                    autocomplete='new-password'
                />
            </FieldLayout>

            {isSubtenant && (
                <Textarea
                    name='description'
                    label={lang.DESCRIPTION}
                    rows={8}
                    size={CONTROL_SIZE.m}
                />
            )}
        </LoginInfoForm>
    );
};

export const getLoginInfoStep = (title: string): WizardStep<CompanyUserModel> => ({
    title,
    validate: ({ data, isEdit, stepData }) => new Promise(async(resolve) => {
        let valid = isValid(data => stepValidate(data, isEdit), data);

        if (valid) {
            const item = {
                additionalId: data.companyID || null,
                id: isEdit ? data.id : null,
                type: VacInternalItemType.User,
                name: data.login,
            };

            const exist = await CommonService.isItemExist(item);

            if (exist) {
                stepData.isLoginExist = true;

                valid = false;
            }
        }

        resolve(valid);
    }),
    render: data => <LoginInfo {...data} />,
});


